import React, { Suspense, useState, useEffect, useRef, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { getInt } from 'utils/functions';
import { Grid } from '@mui/material';
import { Typography, FakeText } from '@totalsoft/rocket-ui';
import Portal from '../../../../utils/portal/Portal';
import { RightsContext } from '../../../../providers/RightsContext';
import { areas } from '../../../../constants/areas';
import { setContent, resetContent } from '../../../../components/layout/RightSideBar';
import { withAreaGetter } from '../../../../providers/AreasConsumers';
import FolderHeader from './FolderHeader';
import { loadFolderWarnings, resetCurrentFolder, loadFolderReports } from '../actionCreators';
import { loadDocumentRights } from 'actions/actionCreators/documentRightsActions';
import { usePrevious } from 'customHooks/usePrevious';
import { showRightSideBar } from '../../../../components/layout/RightSideBar';
import Warnings from 'components/offer/edit/Warnings';
import FolderStepper from './FolderStepper';
import FolderSidebarContainer from '../../sidebar/FolderSidebarContainer';
import FolderSwitcher from './FolderSwitcher';
import initialSteps from '../initialSteps';
import FolderSubscriptionArea from 'features/common/components/FolderSubscriptionArea';
import FolderGeneralDataContainer from '../../edit/generalData/components/FolderGeneralDataContainer';
import FolderExposureContainer from '../../edit/exposure/components/FolderExposureContainer';
import FolderScoringContainer from '../../edit/scoring/components/FolderScoringContainer';
import FolderScoringHistoryContainer from 'features/folder/edit/scoringHistory/components/FolderScoringHistoryContainer';
import FolderDecisionContainer from '../../edit/decision/components/FolderDecisionContainer';
import FolderContractGenerationContainer from '../../edit/contractGeneration/components/FolderContractGenerationContainer';
import documentTypeIds from 'constants/documentTypeIds';
import OfferReportsContainer from 'components/offer/edit/reports/OfferReportsContainer';
import { folderSteps, getDocumentRightsByStateKey } from 'constants/common';
import { dismissDocumentWorkflowError } from 'features/document/edit/documents/actionCreators';
import { ApplicationFeaturesContext } from 'providers/ApplicationFeaturesContext';
import { loadDocumentScoringHistory } from 'features/document/edit/scoringHistory/actionCreators';
import rightSidebarDrawers from 'constants/rightSidebarDrawers';
import { useTitle } from 'utils/hooks/useTitle';
import DocumentCommentsContainer from 'features/offer/edit/sidebar/components/comments/DocumentCommentsContainer';

const FolderWizard = ({
  t,
  documentId,
  siteId,
  folderHeader,
  generalInfo,
  actions,
  isNewFolder,
  isNewFacilityApproval,
  history,
  getElemByAreaKey,
  rights,
  locationPathName,
  warnings,
  warningsIsValid,
  warningsIsLoaded,
  rightSidebarDrawer,
  sessionId,
  historyIsLoaded
}) => {
  const mounted = useRef(false);
  const { folderWithChecklist, enableAdvancedContractGeneration } = useContext(ApplicationFeaturesContext);
  let firstUpdate = false;

  const { documentTypeId, stateId } = folderHeader;
  const isFacilityApproval = documentTypeId === documentTypeIds.FacilityApproval || isNewFacilityApproval;
  const [activeStep, setActiveStep] = useState(0);
  const [localLastSavedStep, setLocalLastSavedStep] = useState(0);
  const [steps, setSteps] = useState(
    initialSteps(
      isNewFacilityApproval ? documentTypeIds.FacilityApproval : documentTypeId,
      generalInfo.showScoring,
      enableAdvancedContractGeneration,
      rights.isImage,
      sessionId
    )
  );
  const [warningsActiveTab, setWarningsActiveTab] = useState(0);
  const [openFolderActions, setOpenFolderActions] = useState(false);

  const prevDocumentId = usePrevious(documentId);
  const prevSiteId = usePrevious(siteId);
  const prevFolderHeaderActiveStep = usePrevious(folderHeader.activeStep);
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [dirtyComment, setDirtyComment] = useState(false);

  const handleOpenFolderActions = event => {
    setOpenFolderActions(true);
    setAnchorEl(event.currentTarget);
  };
  const handleCloseFolderActions = () => setOpenFolderActions(false);

  const setSidebarContent = useCallback(() => {
    switch (rightSidebarDrawer) {
      case rightSidebarDrawers.summary:
        if (dirtyComment) {
          if (window.confirm(t('LeavingWithoutSavingComment'))) {
            setDirtyComment(false);
            setContent(
              <RightsContext.Provider value={rights}>
                <FolderSidebarContainer documentId={documentId} siteId={siteId} />{' '}
              </RightsContext.Provider>
            );
          } else break;
        }
        setContent(
          <RightsContext.Provider value={rights}>
            <FolderSidebarContainer documentId={documentId} siteId={siteId} />{' '}
          </RightsContext.Provider>
        );
        break;
      case rightSidebarDrawers.comments:
        setContent(
          <RightsContext.Provider value={rights}>
            <DocumentCommentsContainer
              documentId={documentId}
              siteId={siteId}
              history={history}
              fullScreen={false}
              dirtyComment={dirtyComment}
              setDirtyComment={setDirtyComment}
            />
          </RightsContext.Provider>
        );
        break;
      default:
        break;
    }
  }, [rightSidebarDrawer, dirtyComment, documentId, siteId, rights, history, t]);

  useEffect(() => {
    mounted.current = true;
    if (!isNewFolder && !isNewFacilityApproval) {
      actions.loadFolderWarnings(documentId, siteId);
      if (!mounted.current) {
        return;
      }
      changeStep(getInt(folderHeader.activeStep));
      setSidebarContent();
    } else {
      checkTransitions();
    }

    actions.loadDocumentRights(documentId, siteId, stateId, documentTypeId);
    showRightSideBar(true);
    firstUpdate = true; // eslint-disable-line react-hooks/exhaustive-deps
    return () => {
      mounted.current = false;
      actions.resetCurrentFolder(documentId, siteId);
      resetContent();
      showRightSideBar(false);
    };
  }, []);

  useEffect(() => {
    const hasRightsToLoadScoringHistory =
      rights.isLoaded &&
      rights.isImage &&
      rights.scoringScoringSummaryRead &&
      rights.scoringKoRead &&
      rights.scoringGridDocumentRead &&
      rights.scoringGridClientRead &&
      rights.scoringGridAssetRead;

    if (!hasRightsToLoadScoringHistory) return;

    actions.loadDocumentScoringHistory(documentId, siteId);
  }, [
    rights.isLoaded,
    rights.isImage,
    rights.scoringScoringSummaryRead,
    rights.scoringKoRead,
    rights.scoringGridDocumentRead,
    rights.scoringGridClientRead,
    rights.scoringGridAssetRead,
    actions,
    documentId,
    siteId
  ]);

  useEffect(() => {
    if (firstUpdate || !mounted.current || rights.isLoaded) return;

    actions.loadDocumentRights(documentId, siteId, stateId, documentTypeId);
  }, [actions, firstUpdate, documentTypeId, isNewFolder, isNewFacilityApproval, documentId, siteId, stateId, rights.isLoaded]);

  useEffect(() => {
    if (firstUpdate || !mounted.current) return;
    actions.resetCurrentFolder(prevDocumentId, prevSiteId);
    if (!isNewFolder && !isNewFacilityApproval) {
      changeStep(getInt(folderHeader.activeStep));
      setSidebarContent();
      showRightSideBar(true);
    }
  }, [documentId, siteId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!rights) return;
    if (!isNewFolder && !isNewFacilityApproval) {
      setSidebarContent();
    }
  }, [isNewFacilityApproval, isNewFolder, rights, setSidebarContent]);

  useEffect(() => {
    if (warningsIsLoaded) {
      if (warnings.filter(x => x.messageTypeId === 1).length > 0) setWarningsActiveTab(1);
      if (warnings.filter(x => x.messageTypeId === 2).length > 0) setWarningsActiveTab(2);
    }
  }, [warningsIsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (firstUpdate || !mounted.current) return;

    const step = steps.find(a => locationPathName.includes(a.route.replace('{id.documentId}', documentId).replace('{id.siteId}', siteId)));

    if (step) {
      setActiveStep(step.id);
    } else {
      changeStep(activeStep);
    }
  }, [locationPathName]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (firstUpdate || !mounted.current || warningsIsValid) return;

    if (Number.isInteger(documentId) && Number.isInteger(siteId)) {
      actions.loadFolderWarnings(documentId, siteId);
    }
  }, [warningsIsValid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (firstUpdate || !mounted.current || rights.isLoaded) return;

    actions.loadDocumentRights(documentId, siteId, stateId, documentTypeId);
    if (
      rights.isImage &&
      rights.scoringScoringSummaryRead &&
      rights.scoringKoRead &&
      rights.scoringGridDocumentRead &&
      rights.scoringGridClientRead &&
      rights.scoringGridAssetRead
    )
      actions.loadDocumentScoringHistory(documentId, siteId);
  }, [
    actions,
    firstUpdate,
    documentTypeId,
    isNewFolder,
    isNewFacilityApproval,
    documentId,
    siteId,
    rights.isLoaded,
    stateId,
    rights.isImage,
    rights.scoringScoringSummaryRead,
    rights.scoringKoRead,
    rights.scoringGridDocumentRead,
    rights.scoringGridClientRead,
    rights.scoringGridAssetRead
  ]);

  useEffect(() => {
    if (generalInfo.loaded && rights && rights.isLoaded && historyIsLoaded)
      setSteps(
        initialSteps(
          isNewFacilityApproval ? documentTypeIds.FacilityApproval : documentTypeId,
          generalInfo.showScoring,
          enableAdvancedContractGeneration,
          rights.isImage,
          sessionId
        )
      );
  }, [generalInfo.loaded, rights, historyIsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (firstUpdate || !mounted.current) return;

    if (folderHeader.activeStep !== prevFolderHeaderActiveStep) {
      checkTransitions(getInt(folderHeader.activeStep));
    }
  }, [folderHeader]); // eslint-disable-line react-hooks/exhaustive-deps

  const onChangeActiveStep = value => {
    setActiveStep(value);
  };

  const changeStep = stepId => {
    onChangeActiveStep(stepId);
    checkTransitions(stepId);
    const step = steps.find(a => a.id === stepId);
    if (step) {
      const defaultRoute = `/folders/${documentId}/${siteId}`;
      if (
        defaultRoute.toLowerCase() === locationPathName.toLowerCase() ||
        defaultRoute.toLowerCase() + '/' === locationPathName.toLowerCase()
      ) {
        onStepClick(step, true);
      } else {
        const urlStep = steps.find(a =>
          locationPathName
            .toLowerCase()
            .includes(a.route.replace('{id.documentId}', documentId).replace('{id.siteId}', siteId).toLowerCase())
        );
        if (urlStep) {
          if (urlStep.disabled) {
            onStepClick(step);
          } else {
            onChangeActiveStep(urlStep.id);
            onStepClick(urlStep);
          }
        } else {
          onChangeActiveStep(-1);
        }
      }
    }
  };

  const onStepClick = (step, isRedirect = false) => {
    if (!documentId) return;

    const route = step.route.replace('{id.documentId}', documentId).replace('{id.siteId}', siteId);

    if (locationPathName === route) return;

    if (isRedirect) {
      history.replace(route);
    } else {
      history.push(route);
    }
  };

  const checkTransitions = lastSavedStep => {
    var localSteps = steps.slice();
    let tempSavedStep = lastSavedStep || localLastSavedStep;

    if (tempSavedStep < 0) tempSavedStep = localLastSavedStep;

    if (isNewFolder || isNewFacilityApproval) {
      for (let i = 1; i < localSteps.length; i++) {
        localSteps[i].disabled = true;
      }
    } else {
      if (isFacilityApproval) {
        for (let i = 1; i < localSteps.length; i++) {
          localSteps[i].disabled = false;
        }
      } else {
        switch (tempSavedStep) {
          case 0:
            localSteps.forEach(item => (item.disabled = item.id <= folderSteps.fourthStep.id ? false : true));
            break;
          case 1:
          case 2:
          case 3:
            localSteps.forEach(item => (item.disabled = item.id > folderSteps.decisionStep.id ? true : false));
            break;
          case 4:
            localSteps.forEach(item => (item.disabled = item.id > folderSteps.fourthStep.id ? true : false));
            break;
          default:
            localSteps.forEach(item => (item.disabled = true));
        }
      }
    }

    setSteps(localSteps);
    setLocalLastSavedStep(tempSavedStep);
  };

  const loadFolderReports = (documentId, siteId) => {
    actions.loadFolderReports(documentId, siteId);
  };

  const onChangeShowReportsModal = value => {
    setShowReportsModal(value);
  };

  const titleData = {
    folderNumber: folderHeader.folderNumber || '',
    clientName: folderHeader.client?.clientName || '',
    hasProperties: folderHeader.hasProperties,
    stateName: folderHeader.stateName || '',
    folderDate: folderHeader.folderDate || ''
  };

  const headerContent =
    !isNewFolder && !isNewFacilityApproval ? (
      <FolderHeader
        documentId={getInt(documentId)}
        siteId={getInt(siteId)}
        data={titleData}
        history={history}
        onChangeActiveStep={onChangeActiveStep}
        loadFolderReports={loadFolderReports}
        onChangeShowReportsModal={onChangeShowReportsModal}
        folderWithChecklist={folderWithChecklist}
        isImage={rights.isImage}
        openFolderActions={openFolderActions}
        handleCloseFolderActions={handleCloseFolderActions}
        handleOpenFolderActions={handleOpenFolderActions}
        viewWorkflow={rights.viewWorkflow}
        clientVerificationsRead={rights.clientVerificationsRead}
        documentPropertiesRead={rights.documentPropertiesRead}
        documentReportsRead={rights.documentReportsRead}
        documentChecklistRead={rights.documentChecklistRead}
        otherStateTransitionHistoryRead={rights.otherStateTransitionHistoryRead}
        isFacilityApproval={isFacilityApproval}
        anchorEl={anchorEl}
      />
    ) : isNewFacilityApproval ? (
      <Typography variant='subtitle1' fontWeight='bold' whiteSpace='nowrap'>
        {t('Folder.NewFacilityApprovalPageTitle')}
      </Typography>
    ) : (
      <Typography variant='subtitle1' fontWeight='bold' whiteSpace='nowrap'>
        {t('Folder.NewFolderPageTitle')}
      </Typography>
    );

  useTitle(
    isNewFolder
      ? t('Folder.NewFolderPageTitle')
      : isNewFacilityApproval
      ? t('Folder.NewFacilityApprovalPageTitle')
      : t('Folder.PageTitle', { data: titleData })
  );

  const folderGeneralDataContainer = () => (
    <FolderGeneralDataContainer
      documentId={documentId}
      siteId={siteId}
      history={history}
      isFacilityApproval={isFacilityApproval}
      documentTypeId={documentTypeId}
      masterPartnerApp={rights.masterPartnerApp && rights.masterPartnerRead}
    />
  );

  const createFolderContainer = () => (
    <FolderGeneralDataContainer isFacilityApproval={isFacilityApproval} history={history} documentTypeId={documentTypeId} />
  );

  const folderExposureContainer = () => (
    <FolderExposureContainer
      documentId={documentId}
      siteId={siteId}
      isFacilityApproval={isFacilityApproval}
    />
  );

  const folderScoringContainer = () => (
    <FolderScoringContainer
      documentId={documentId}
      siteId={siteId}
      history={history}
      isFacilityApproval={isFacilityApproval}
    />
  );

  const folderScoringHistoryContainer = () => (
    <FolderScoringHistoryContainer documentId={documentId} siteId={siteId} history={history} />
  );

  const folderDecisionContainer = () => (
    <FolderDecisionContainer
      documentId={documentId}
      siteId={siteId}
      isFacilityApproval={isFacilityApproval}
      facilityId={folderHeader.facilityId}
      facilitySiteId={folderHeader.facilitySiteId}
      offerId={folderHeader.offerId}
      offerSiteId={folderHeader.offerSiteId}
      rights={rights}
    />
  );
  const folderContractGenerationContainer = () => (
    <FolderContractGenerationContainer documentId={documentId} siteId={siteId} />
  );

  const handleDismissDocumentWorkflowError = useCallback(
    documentWorkflowErrorId => () => {
      if (!warningsIsLoaded) return;
      async function onDismissAsync() {
        await actions.dismissDocumentWorkflowError(documentWorkflowErrorId);
      }
      onDismissAsync();
    },
    [actions, warningsIsLoaded]
  );

  return (
    <div>
      <FolderSubscriptionArea />
      <RightsContext.Provider value={rights}>
        <Grid container spacing={2} px={2}>
          <Grid item xs={12}>
            <FolderStepper steps={steps} onStepClick={onStepClick} activeStep={activeStep} isImage={rights.isImage} />
          </Grid>
          {warnings && warnings.length > 0 && (
            <Grid item xs={12}>
              <Warnings
                warnings={warnings}
                warningsActiveTab={warningsActiveTab}
                setWarningsActiveTab={setWarningsActiveTab}
                handleDismissDocumentWorkflowError={handleDismissDocumentWorkflowError}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Suspense fallback={<FakeText lines={10} onPaper />}>
              <FolderSwitcher
                folderGeneralDataContainer={folderGeneralDataContainer}
                createFolderContainer={createFolderContainer}
                folderExposureContainer={folderExposureContainer}
                folderScoringContainer={folderScoringContainer}
                folderScoringHistoryContainer={folderScoringHistoryContainer}
                folderDecisionContainer={folderDecisionContainer}
                folderContractGenerationContainer={folderContractGenerationContainer}
                onChangeActiveStep={onChangeActiveStep}
                history={history}
              />
            </Suspense>
          </Grid>
          {showReportsModal && (
            <OfferReportsContainer documentId={documentId} siteId={siteId} onHandleShowReportsModal={onChangeShowReportsModal} />
          )}
        </Grid>
        <Portal container={getElemByAreaKey(areas.header)}>{headerContent}</Portal>
      </RightsContext.Provider>
    </div>
  );
};

FolderWizard.propTypes = {
  t: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  folderHeader: PropTypes.object,
  generalInfo: PropTypes.object,
  actions: PropTypes.object.isRequired,
  rights: PropTypes.object,
  warnings: PropTypes.array,
  warningsIsValid: PropTypes.bool.isRequired,
  warningsIsLoaded: PropTypes.bool,
  rightSidebarDrawer: PropTypes.string,
  documentId: PropTypes.number,
  siteId: PropTypes.number,
  isNewFolder: PropTypes.bool,
  isNewFacilityApproval: PropTypes.bool,
  history: PropTypes.object.isRequired,
  getElemByAreaKey: PropTypes.func.isRequired,
  locationPathName: PropTypes.string,
  sessionId: PropTypes.string,
  historyIsLoaded: PropTypes.bool
};


function mapStateToProps(state, props) {
  const { folderHeader } = props;
  const { isValid, loaded } = state.currentFolder.warnings;
  const rightsStateKey = getDocumentRightsByStateKey(folderHeader.stateId, folderHeader.documentTypeId);
  const rights = state.documentRights[rightsStateKey] || {};
  const key = `${folderHeader.documentId}-${folderHeader.siteId}`;
  const historyIsLoaded = state.document.scoringHistoryData[key]?.loaded;
  const sessionId = state.document.scoringHistoryData[key]?.scoringSessionsHistory[0];
  const { sidebarDrawer } = state.currentFolder.sidebarData;

  return {
    rights,
    warnings: state.currentFolder.warnings,
    warningsIsValid: isValid,
    warningsIsLoaded: loaded,
    rightSidebarDrawer: sidebarDrawer,
    sessionId: sessionId,
    historyIsLoaded
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        resetCurrentFolder,
        loadFolderWarnings,
        loadFolderReports,
        dismissDocumentWorkflowError,
        loadDocumentRights,
        loadDocumentScoringHistory
      },
      dispatch
    ),
    dispatch
  };
}

export default compose(connect(mapStateToProps, mapDispatchToProps), withAreaGetter, withTranslation())(FolderWizard);
